"use client";

import { Button } from "@decibeltrade/web-core/components";
import { useTranslations } from "next-intl";
import { css } from "styled-system/css";
import { flex } from "styled-system/patterns";

export interface DetailsHeaderProps {
  useCreditDisabled: boolean;
  useCreditLoading: boolean;
  onUseCredit: () => void;
  creditsExhausted?: boolean;
  /**
   * While the trial is past expiry and unsettled, the header CTA becomes the
   * user-side keeper backup instead of Use Credit.
   */
  settleNow?: { pending: boolean; onSettle: () => void; resubmit: boolean };
}

export function DetailsHeader({
  useCreditDisabled,
  useCreditLoading,
  onUseCredit,
  creditsExhausted = false,
  settleNow,
}: DetailsHeaderProps) {
  const t = useTranslations("firstTradeOnUs.details");

  return (
    <div className={headerStyles} data-testid="fft-details-header">
      <h2 className={titleStyles}>{t("title")}</h2>
      {settleNow ? (
        <Button
          variant="primary"
          size="md"
          loading={settleNow.pending}
          onClick={settleNow.onSettle}
          data-testid="fft-details-settle-now"
        >
          {t(settleNow.resubmit ? "finalizeCta" : "settleNowCta")}
        </Button>
      ) : (
        <Button
          variant={creditsExhausted ? "secondary" : "primary"}
          size="md"
          loading={useCreditLoading}
          disabled={useCreditDisabled}
          onClick={onUseCredit}
          data-testid="fft-details-use-credit"
        >
          {creditsExhausted ? t("noMoreCreditsCta") : t("useCreditCta")}
        </Button>
      )}
    </div>
  );
}

const headerStyles = flex({
  align: "center",
  justify: "space-between",
  gap: "12",
  w: "full",
  borderBottomWidth: "100",
  borderBottomStyle: "solid",
  borderBottomColor: "border.primary",
  pt: "16",
  px: "16",
  pb: "12",
});

const titleStyles = css({ textStyle: "heading.xs", color: "text.primary" });
